import { usePositionRequests } from '@src/hooks/usePositionRequests';
import { useSubAccounts } from '@src/providers';
import {
  DateTimePicker,
  Dialog,
  EMPTY_ARRAY,
  FetchError,
  FormGroup,
  NOW,
  NotificationVariants,
  SearchSelect,
  formattedUTCDate,
  getFetchErrorUserMessage,
  useDynamicCallback,
  useGlobalToasts,
  type DialogProps,
} from '@talos/kyoko';
import { useEffect, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';
import {
  getRecalcNowTransactTime,
  getRecalcTransactTimeBounds,
  normalizeRecalcTransactTime,
} from './recalcTransactTimeBounds';

interface RecalculateSubAccountHistoryForm {
  startTime?: Date;
  subAccount?: string;
}

interface RecalculateSubAccountHistoryDialogProps {
  dialogProps: DialogProps;
}

export function RecalculateSubAccountHistoryDialog({ dialogProps }: RecalculateSubAccountHistoryDialogProps) {
  const { add: addToast } = useGlobalToasts();
  const { recalculateSubAccountHistory } = usePositionRequests();
  const { allSubAccountBooks, subAccountsByName } = useSubAccounts();
  const [form, setForm] = useState<RecalculateSubAccountHistoryForm>({});
  const [touched, setTouched] = useState<{ startTime?: boolean }>({});
  const [transactTimeBounds, setTransactTimeBounds] = useState(() => getRecalcTransactTimeBounds(new Date()));

  const { minValue, maxValue } = transactTimeBounds;

  useEffect(() => {
    if (dialogProps.isOpen) {
      setForm({});
      setTouched({});
      setTransactTimeBounds(getRecalcTransactTimeBounds(new Date()));
    }
  }, [dialogProps.isOpen]);

  const subAccountOptions = useMemo(
    () => allSubAccountBooks?.map(subAccount => subAccount.Name) ?? EMPTY_ARRAY,
    [allSubAccountBooks]
  );

  const getSubAccountLabel = useDynamicCallback((subAccountName: string | undefined) =>
    subAccountName ? (subAccountsByName?.get(subAccountName)?.DisplayName ?? subAccountName) : (subAccountName ?? '')
  );

  const startTimeError = touched.startTime && form.startTime == null ? 'Start time is required' : undefined;
  const recalculateAllSubAccounts = form.subAccount == null;

  const handleStartTimeChange = useDynamicCallback((value: Date | null) => {
    setTouched(prev => ({ ...prev, startTime: true }));
    setForm(prev => ({
      ...prev,
      startTime: value == null ? undefined : normalizeRecalcTransactTime(value, new Date()),
    }));
  });

  const recalcShortcutResolver = useDynamicCallback((shortcut: string) => {
    if (shortcut === NOW) {
      return getRecalcNowTransactTime(new Date());
    }
    return null;
  });

  const handleSubAccountChange = useDynamicCallback((subAccount?: string) => {
    setForm(prev => ({ ...prev, subAccount }));
  });

  const handleConfirm = useDynamicCallback(() => {
    if (form.startTime == null) {
      return;
    }

    const startTime = normalizeRecalcTransactTime(form.startTime, new Date());
    const params = {
      TransactTime: startTime.toISOString(),
      ExternalID: uuid(),
      ...(form.subAccount != null ? { AccountName: form.subAccount } : {}),
    };

    recalculateSubAccountHistory(params)
      .then(() => {
        addToast({
          text: `Recalculation requested as of ${formattedUTCDate(startTime, '{yyyy}-{MM}-{dd} {HH}:{mm}')} UTC.`,
          variant: NotificationVariants.Positive,
        });
      })
      .catch((error: unknown) => {
        const message =
          error instanceof FetchError
            ? getFetchErrorUserMessage(error, 'Request failed')
            : error instanceof Error
              ? error.message
              : String(error);
        addToast({
          text: `Failed to request recalc: ${message}`,
          variant: NotificationVariants.Negative,
        });
      });
  });

  return (
    <Dialog
      {...dialogProps}
      title="Recalculate Sub Account History"
      width={400}
      confirmLabel={recalculateAllSubAccounts ? 'Recalculate All' : 'Recalculate'}
      confirmDisabled={form.startTime == null}
      onConfirm={handleConfirm}
    >
      <FormGroup
        label="Start time*"
        error={startTimeError}
        help="Must be within the last 14 days."
        withMessageSpacing
        mb="spacing16"
      >
        <DateTimePicker
          value={form.startTime ?? null}
          onChange={handleStartTimeChange}
          minValue={minValue}
          maxValue={maxValue}
          shortcuts={{ [NOW]: 'Now' }}
          customShortcutResolver={recalcShortcutResolver}
          timePickerVariant="selector"
          timeSelectorIntervalMinutes={15}
          data-testid="recalculate-sub-account-history-start-time"
        />
      </FormGroup>

      <FormGroup label="Sub Account" mb="spacing16">
        <SearchSelect
          selection={form.subAccount}
          options={subAccountOptions}
          onChange={handleSubAccountChange}
          getLabel={getSubAccountLabel}
          placeholder="All sub accounts"
          showClear
          data-testid="recalculate-sub-account-history-sub-account"
        />
      </FormGroup>
    </Dialog>
  );
}
